<template>
  <v-container
    class="align-content-start"
    fill-height
    fluid
  >
    <v-row>
      <section-header
        :breadcrumb-items="breadcrumb"
        :button-title="$t('Assumptions.EditAssumptions.Title')"
        :button-icon="''"
        button
        button-to="EditAssumptions"
        :query="{from: activeTab, intra: interestRateSection}"
        :is-draft-status="isDraftStatus"
        :save-button-title="saveButtonTitle"
        @click-cancel="handleCancel"
        @click-save="handleSave"
      />
    </v-row>

    <v-row>
      <v-col
        cols="10"
      >
        <div class="d-flex align-center justify-space-between">
          <v-tabs
            v-model="activeTab"
            active-class="active-tab"
            background-color="transparent"
            height="30"
            hide-slider
            show-arrows
          >
            <v-tab :href="`#${$t('EVE.Tab.RegulatoryThresholds.Slug')}`">
              {{ $t('EVE.Tab.RegulatoryThresholds.Title') }}
            </v-tab>
            <v-tab :href="`#${$t('EVE.Tab.InternalThresholds.Slug')}`">
              {{ $t('EVE.Tab.InternalThresholds.Title') }}
            </v-tab>
            <v-tab :href="`#${$t('EVE.Tab.BaselTests.Slug')}`">
              {{ $t('EVE.Tab.BaselTests.Title') }}
            </v-tab>
            <v-tab :href="`#${$t('EVE.Tab.HistoricalEVEChanges.Slug')}`">
              {{ $t('EVE.Tab.HistoricalEVEChanges.Title') }}
            </v-tab>
            <v-tab :href="`#${$t('EVE.Tab.SharpestEVEChanges.Slug')}`">
              {{ $t('EVE.Tab.SharpestEVEChanges.Title') }}
            </v-tab>
          </v-tabs>
        </div>
      </v-col>
      <v-col
        v-if="activeEveAnalysis.ValueDateFmt"
        cols="2"
        class="d-flex justify-end"
      >
        <span class="portfolio-value">
          {{ $t('Common.Date.Selector.Label') }} <span>{{ activeEveAnalysis.ValueDateFmt }}</span>
        </span>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-tabs-items
          v-model="activeTab"
          class="fill-height"
        >
          <v-tab-item :value="$t('EVE.Tab.RegulatoryThresholds.Slug')">
            <regulatory-thresholds :data="activeEveAnalysis" />
          </v-tab-item>
          <v-tab-item :value="$t('EVE.Tab.InternalThresholds.Slug')">
            <internal-thresholds :data="activeEveAnalysis" />
          </v-tab-item>
          <v-tab-item :value="$t('EVE.Tab.BaselTests.Slug')">
            <basel-tests :data="activeEveAnalysis" />
          </v-tab-item>
          <v-tab-item :value="$t('EVE.Tab.HistoricalEVEChanges.Slug')">
            <historical-eve-changes :data="activeEveAnalysis" />
          </v-tab-item>
          <v-tab-item :value="$t('EVE.Tab.SharpestEVEChanges.Slug')">
            <sharpest-eve-changes :data="activeEveAnalysis" />
          </v-tab-item>
        </v-tabs-items>
      </v-col>
    </v-row>

    <!-- Save Assumptions Modal -->
    <save-assumptions-modal
      :open="showSaveModal"
      :on-close="closeSaveModal"
      :on-save="handleSaveAssumptions"
      :is-saving="isSavingAssumptions"
      :default-name="activeEveAnalysis.AnalysisName || ''"
    />
  </v-container>
</template>

<script>
import { API } from '@/api/Api'
import i18nRouteMeta                    from '@/mixins/i18n/i18nRouteMeta'
import SectionHeader                    from '@/theme/default/components/common/SectionHeader'
import SaveAssumptionsModal             from '@/theme/default/components/common/SaveAssumptionsModal'
import RegulatoryThresholds             from './EVE/RegulatoryThresholds'
import InternalThresholds               from './EVE/InternalThresholds'
import BaselTests                       from './EVE/BaselTests'
import HistoricalEVEChanges             from './EVE/HistoricalEVEChanges'
import SharpestEVEChanges               from './EVE/SharpestEVEChanges'
import RiskActiveEveAnalysisResource    from '@/api/resources/risks/RiskActiveEveAnalysisResource'
import routerTabState                    from '@/mixins/routerTabState'

export default {
  name      : 'EVE',
  components: {
    SectionHeader,
    SaveAssumptionsModal,
    'regulatory-thresholds' : RegulatoryThresholds,
    'internal-thresholds'   : InternalThresholds,
    'basel-tests'           : BaselTests,
    'historical-eve-changes': HistoricalEVEChanges,
    'sharpest-eve-changes'  : SharpestEVEChanges
  },
  mixins   : [i18nRouteMeta, routerTabState],
  dataStore: {
    appToolbarExtended: 'App.Config.AppToolbarExtended',
    ActiveEveAnalysis : 'RiskInterestRate.ActiveEveAnalysis',
    currentAnalysisId : 'ActiveAnalysis.currentAnalysisId',
    previousAnalysisId: 'ActiveAnalysis.previousAnalysisId',
    isDraftAnalysis   : 'ActiveAnalysis.isDraft'
  },
  data () {
    return {
      breakpoint         : (this.appToolbarExtended && this.$vuetify.breakpoint.smAndDown ? 12 : 6),
      minBlockHeight     : 364,
      editMode           : false,
      analysisId         : null,
      interestRateSection: 'EVE',
      showSaveModal      : false,
      isSavingAssumptions: false
    }
  },
  computed: {
    isDraftStatus () {
      return this.isDraftAnalysis
    },
    saveButtonTitle () {
      return this.$t('Assumptions.EditAssumptions.Tabs.Button.SaveAssumptions')
    },
    breadcrumb () {
      return [
        {
          text    : this.$t('Route.Risks.Title'),
          disabled: true
        },
        {
          text    : this.$t('Route.InterestRate.Title'),
          disabled: true
        },
        {
          text    : this.activeEveAnalysis.AnalysisName ?? 'Loading...',
          disabled: true
        },
        {
          text    : this.$t('EVE.HeaderTitle'),
          disabled: true
        }
      ]
    },
    accountId () {
      return this.user?.selectedAccountId || null
    },
    // Get data from the dataStore (same as Rates.vue)
    activeEveAnalysis () {
      return this.ActiveEveAnalysis.Data || null
    }
  },
  watch: {
    appToolbarExtended (val) {
      this.breakpoint = val && this.$vuetify.breakpoint.smAndDown ? 12 : 6
    },

    '$vuetify.breakpoint.smAndDown': {
      handler (val) {
        this.breakpoint = val && this.appToolbarExtended ? 12 : 6
      }
    },
    activeTab: {
      immediate: true,
      handler (newTab) {
        if (!newTab) {
          this.activeTab = this.$t('EVE.Tab.RegulatoryThresholds.Slug')
        }
      }
    }
  },
  created () {
    this.activeEveAnalysisData()
  },
  methods: {
    handleCancel () {
      this.currentAnalysisId = this.previousAnalysisId
      this.previousAnalysisId = null
      this.isDraftAnalysis = false
      this.activeEveAnalysisData()
    },

    handleSave () {
      this.showSaveModal = true
    },

    closeSaveModal () {
      this.showSaveModal = false
    },

    async handleSaveAssumptions (assumptionName) {
      this.isSavingAssumptions = true

      try {
        const response = await API.Resource.InterestRate.SaveBankAnalysisAssumptions(
          this.accountId,
          this.currentAnalysisId,
          assumptionName
        )

        if (API.isResponseSuccess(response)) {
          this.showSaveModal = false

          // finish draft state
          this.previousAnalysisId = null

          // You can add a success message or redirect here
          // alert('Assumptions saved successfully! should redirect to saved items list when given')

          await this.$router.push({ name: 'PastAnalyses' })
        } else {
          const errors = API.responseErrors(response) || []
          this.$bus.$emit('app:error', errors)
        }
      } catch (error) {
        this.$bus.$emit('app:error', ['Failed to save assumptions'])
      } finally {
        this.isDraftAnalysis = false
        this.isSavingAssumptions = false
      }
    },

    async activeEveAnalysisData () {
      const analysisId = this.currentAnalysisId
      const response = await API.Resource.RiskInterestRate.ActiveEveAnalysis(this.accountId, analysisId)
      this.handleResponse(response, 'activeEveAnalysis')
    },

    handleResponse (response, key) {
      const data = API.responseData(response)?.[key] || null
      const errors = API.responseErrors(response) || []

      if (API.isResponseSuccess(response) && data) {
        if (!this.currentAnalysisId) {
          this.currentAnalysisId = data.id
        }

        this.ActiveEveAnalysis.Data = RiskActiveEveAnalysisResource.fromApiResponse(data)
      } else {
        this.$bus.$emit('app:error', errors)
      }
    }
  }
}
</script>

<style scoped>
.portfolio-value {
  margin-top: 4px;
  text-align: right;
  font-size: 12px;
  font-weight: 500;
  color:var(--v-synthesis-ui-grey-01-base);
  span {
    font-weight: 700;
  }
}

</style>
